import type {
  StatementNodeAttributes,
  StatementNodeData,
  StatementNodeType,
  StatementSegment,
} from "./types";

const KNOWN_NODE_TYPES: readonly StatementNodeType[] = [
  "StatementNode",
  "GlossaryStatementNode",
] as const;

const looksLikeStatementXml = (input: string): boolean =>
  /<(StatementNode|GlossaryStatementNode)\b/i.test(input);

const parseBoolean = (value: string | undefined): boolean | undefined => {
  if (value === undefined) return undefined;
  if (value !== undefined && ["true", "1", "yes"].includes(value.toLowerCase()))
    return true;
  if (value !== undefined && ["false", "0", "no"].includes(value.toLowerCase()))
    return false;
  return undefined;
};

const normalizeNewlines = (value: string): string =>
  value.replace(/\r\n/g, "\n");

const isWhitespaceOnly = (value: string): boolean => value.trim().length === 0;

const nextMeaningfulKind = (
  segments: StatementSegment[],
  startIndex: number,
): StatementSegment["kind"] | null => {
  for (let i = startIndex; i < segments.length; i += 1) {
    const segment = segments[i];
    if (segment.kind === "node") return "node";
    if (segment.text && !isWhitespaceOnly(segment.text)) return "text";
  }
  return null;
};

const normalizeInterSegmentWhitespace = (
  segments: StatementSegment[],
): StatementSegment[] => {
  const result: StatementSegment[] = [];

  for (let i = 0; i < segments.length; i += 1) {
    const segment = segments[i];

    if (segment.kind === "node") {
      result.push(segment);
      continue;
    }

    if (!segment.text) continue;

    if (!isWhitespaceOnly(segment.text)) {
      result.push({ kind: "text", text: segment.text });
      continue;
    }

    const prevMeaningful = (() => {
      for (let j = result.length - 1; j >= 0; j -= 1) {
        const prev = result[j];
        if (prev.kind === "node") return "node";
        if (prev.text && !isWhitespaceOnly(prev.text)) return "text";
      }
      return null;
    })();

    const nextMeaningful = nextMeaningfulKind(segments, i + 1);

    if (!prevMeaningful || !nextMeaningful) {
      continue;
    }

    const last = result[result.length - 1];
    if (last?.kind === "text" && /\s$/.test(last.text)) continue;
    result.push({ kind: "text", text: " " });
  }

  return result;
};

const coerceNode = (
  type: StatementNodeType,
  text: string,
  attributes: StatementNodeAttributes,
): StatementNodeData => ({
  type,
  text: normalizeNewlines(text),
  attributes,
  id: attributes.id,
  isEdited: parseBoolean(attributes.isEdited),
  code: attributes.code,
  groupId: attributes.groupId,
  hasVariable: parseBoolean(attributes.hasVariable),
  variantId: attributes.variantId,
});

const segmentsToPlainText = (segments: StatementSegment[]): string =>
  segments
    .map((segment) =>
      segment.kind === "text" ? segment.text : segment.node.text,
    )
    .join("");

const glossaryNodeHasMissingRequiredFields = (
  node: StatementNodeData,
): boolean => {
  if (node.type !== "GlossaryStatementNode") return false;

  const hasCode = (node.attributes.code || "").trim().length > 0;
  const hasGroupId = (node.attributes.groupId || "").trim().length > 0;
  const hasVariantId = (node.attributes.variantId || "").trim().length > 0;
  const hasValidHasVariable =
    parseBoolean(node.attributes.hasVariable) !== undefined;

  return !hasCode || !hasGroupId || !hasValidHasVariable || !hasVariantId;
};

const parseWithDomParser = (input: string): StatementSegment[] | null => {
  if (typeof DOMParser === "undefined") return null;

  const wrapped = `<Root>${input}</Root>`;
  const doc = new DOMParser().parseFromString(wrapped, "application/xml");

  if (doc.getElementsByTagName("parsererror").length > 0) return null;
  let root: Element = doc.documentElement;
  if (!root) return null;
  const hasOutsideText = (child: Node): boolean =>
    (child.nodeType === Node.TEXT_NODE ||
      child.nodeType === Node.CDATA_SECTION_NODE) &&
    !!(child.textContent || "").trim();
  const wrappers = ["Root", "root", "Statements", "statements"];
  const outerElements = Array.from(root.children);
  if (
    outerElements.length === 1 &&
    wrappers.includes(outerElements[0].tagName) &&
    Array.from(root.childNodes).every((child) => !hasOutsideText(child))
  ) {
    root = outerElements[0];
  }
  // A well-formed but unsupported shape must not be partially displayed and
  // subsequently saved with the unknown content silently removed.
  if (
    Array.from(root.childNodes).some(
      (child) =>
        hasOutsideText(child) ||
        (child.nodeType === Node.ELEMENT_NODE &&
          (!KNOWN_NODE_TYPES.includes(
            (child as Element).tagName as StatementNodeType,
          ) ||
            (child as Element).children.length > 0)),
    )
  )
    return null;

  const segments: StatementSegment[] = [];

  root.childNodes.forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) {
      const text = child.textContent ?? "";
      if (text) segments.push({ kind: "text", text: normalizeNewlines(text) });
      return;
    }

    if (child.nodeType !== Node.ELEMENT_NODE) return;

    const element = child as Element;
    const type = element.tagName as StatementNodeType;
    if (!KNOWN_NODE_TYPES.includes(type)) return;

    const attributes: StatementNodeAttributes = {};
    Array.from(element.attributes).forEach((attr) => {
      attributes[attr.name] = attr.value;
    });

    segments.push({
      kind: "node",
      node: coerceNode(type, element.textContent ?? "", attributes),
    });
  });

  return segments.length > 0 ? segments : null;
};

const parseAttributes = (raw: string): StatementNodeAttributes => {
  const attributes: StatementNodeAttributes = {};
  const attrRegex = /([A-Za-z_][A-Za-z0-9_-]*)\s*=\s*"([^"]*)"/g;
  let match: RegExpExecArray | null;
  while ((match = attrRegex.exec(raw))) {
    attributes[match[1]] = match[2];
  }
  return attributes;
};

const parseWithRegex = (input: string): StatementSegment[] => {
  const segments: StatementSegment[] = [];
  const normalized = normalizeNewlines(input);
  const regex =
    /<(StatementNode|GlossaryStatementNode)\b([^>]*)>([\s\S]*?)<\/\1>/gi;

  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(normalized))) {
    if (match.index > lastIndex) {
      const between = normalized.slice(lastIndex, match.index);
      if (between) segments.push({ kind: "text", text: between });
    }

    const type = match[1] as StatementNodeType;
    const attrs = parseAttributes(match[2] ?? "");
    const text = match[3] ?? "";
    segments.push({ kind: "node", node: coerceNode(type, text, attrs) });

    lastIndex = regex.lastIndex;
  }

  if (lastIndex < normalized.length) {
    const tail = normalized.slice(lastIndex);
    if (tail) segments.push({ kind: "text", text: tail });
  }

  return segments.length > 0 ? segments : [{ kind: "text", text: normalized }];
};

export type StatementXmlAnalysis = {
  segments: StatementSegment[];
  plainText: string;
  hasNodes: boolean;
  hasMissingGlossaryFields: boolean;
};

export const analyzeStatementXmlSegments = (
  value: string | null | undefined,
): StatementXmlAnalysis => {
  const input = normalizeNewlines(value ?? "");
  if (!input) {
    return {
      segments: [],
      plainText: "",
      hasNodes: false,
      hasMissingGlossaryFields: false,
    };
  }
  if (!looksLikeStatementXml(input)) {
    return {
      segments: [{ kind: "text", text: input }],
      plainText: input,
      hasNodes: false,
      hasMissingGlossaryFields: false,
    };
  }

  const domSegments = parseWithDomParser(input);
  if (domSegments) {
    const normalizedDomSegments = normalizeInterSegmentWhitespace(domSegments);
    const plainText = segmentsToPlainText(normalizedDomSegments);
    const hasMissingGlossaryFields = normalizedDomSegments.some(
      (segment) =>
        segment.kind === "node" &&
        glossaryNodeHasMissingRequiredFields(segment.node),
    );

    // Incomplete historical metadata is an attention state, not permission to
    // discard identity. Keep every supplied attribute until explicit correction.

    return {
      segments: normalizedDomSegments,
      plainText,
      hasNodes: normalizedDomSegments.some(
        (segment) => segment.kind === "node",
      ),
      hasMissingGlossaryFields,
    };
  }

  if (typeof DOMParser !== "undefined") {
    // Keep unsupported input intact for correction; the shared server contract
    // reports its actionable error rather than accepting a lossy projection.
    return {
      segments: [{ kind: "text", text: input }],
      plainText: input,
      hasNodes: false,
      hasMissingGlossaryFields: false,
    };
  }
  const regexSegments = normalizeInterSegmentWhitespace(parseWithRegex(input));
  return {
    segments: regexSegments,
    plainText: segmentsToPlainText(regexSegments),
    hasNodes: regexSegments.some((segment) => segment.kind === "node"),
    hasMissingGlossaryFields: false,
  };
};

export const parseStatementXmlSegments = (
  value: string | null | undefined,
): StatementSegment[] => {
  return analyzeStatementXmlSegments(value).segments;
};

export const statementXmlToPlainText = (
  value: string | null | undefined,
): string => {
  return analyzeStatementXmlSegments(value).plainText;
};

const escapeXmlText = (value: string): string =>
  value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const escapeXmlAttrValue = (value: string): string =>
  escapeXmlText(value).replace(/"/g, "&quot;");

export const serializeStatementXmlSegments = (
  segments: StatementSegment[],
): string => {
  return segments
    .map((segment) => {
      if (segment.kind === "text") return segment.text;
      const attrs = Object.entries(segment.node.attributes)
        .map(([key, val]) => ` ${key}="${escapeXmlAttrValue(String(val))}"`)
        .join("");
      return `<${segment.node.type}${attrs}>${escapeXmlText(segment.node.text)}</${segment.node.type}>`;
    })
    .join("");
};
